/**
 * The bubbles on the Texts card: one for a text on the Roof Board line, one
 * for a text that came through a rep's own phone, and the day label above a
 * run of them. Out of TextsPanel.tsx because that file is at the size lint
 * allows (ADR 0310 added the pictures); nothing here changed in the move.
 */

import {
  describeTextFailure,
  textState,
  type ThreadEntry,
} from "@roof-hawk/core";
import { useEffect, useMemo, useState } from "react";

import { t } from "../../i18n";
import {
  signMessageMedia,
  type MessageRow,
  type PersonalTextRow,
} from "../../lib/queries";

export function dayLabel(day: string): string {
  const at = new Date(`${day}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - at.getTime()) / 86_400_000);
  if (days === 0) return t("Today");
  if (days === 1) return t("Yesterday");
  return at.toLocaleDateString();
}

const clockOf = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/**
 * A text that went through somebody's own phone.
 *
 * Badged with that phone, because which number a customer has been talking to
 * decides where their next reply lands, and because since there have been two
 * Macs the badge also answers who to ask about it (ADR 0178). Everybody sees
 * the message; only the person whose phone it came through gets the delete, and
 * deleting is how a message that should not be here leaves.
 */
export function PersonalBubble({
  entry,
  owner,
  onDelete,
}: {
  entry: Extract<
    ThreadEntry<MessageRow, PersonalTextRow>,
    { channel: "personal" }
  >;
  owner: string;
  onDelete: (() => void) | null;
}) {
  return (
    <div className={`textbubble personal${entry.mine ? " mine" : ""}`}>
      <div className="textbody">{entry.text.body}</div>
      <div className="textmeta">
        <span className="badge draft">{owner}</span> {clockOf(entry.sent_at)}
        {onDelete && (
          <>
            {" · "}
            <button type="button" className="linkbtn" onClick={onDelete}>
              {t("Delete")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function TextBubble({
  message,
  line = null,
}: {
  message: MessageRow;
  /** Which of our lines it left from, when the thread has used several. */
  line?: string | null;
}) {
  const state = textState(message);
  const mine = message.direction === "outbound";
  const [media, setMedia] = useState<Record<string, string>>({});

  const paths = useMemo(
    () => (message.media ?? []).map((m) => m.path).filter(Boolean),
    [message.media],
  );

  useEffect(() => {
    if (paths.length === 0) return;
    let live = true;
    signMessageMedia(paths)
      .then((urls) => {
        if (live) setMedia(urls);
      })
      // A picture that will not load is not worth an error banner over the
      // message it came with.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [paths]);

  return (
    <div className={`textbubble${mine ? " mine" : ""}`}>
      {message.body && <div className="textbody">{message.body}</div>}

      {paths.map((path) =>
        media[path] ? (
          <img key={path} src={media[path]} alt={t("Sent in a text")} />
        ) : (
          <div key={path} className="na-hint">
            {t("Picture")}
          </div>
        ),
      )}

      <div className="textmeta">
        {clockOf(message.sent_at)}
        {line ? ` · ${t("from {line}", { line })}` : ""}
        {mine && message.sender?.full_name
          ? ` · ${message.sender.full_name}`
          : ""}
        {state === "sending" ? ` · ${t("sending")}` : ""}
        {state === "sent" ? ` · ${t("sent")}` : ""}
      </div>

      {state === "failed" && (
        // The worst outcome in this whole feature is a text that silently never
        // arrived: the customer thinks they were ignored and the rep thinks
        // they answered. So a failure is said out loud, in words.
        <div className="err" role="alert">
          {t(describeTextFailure(message.error_code, message.error_message))}
        </div>
      )}
    </div>
  );
}
